import React from 'react';
import { Box, Button, alpha, useTheme, Typography, TextField } from '@mui/material';

interface MultipleChoiceFieldProps {
  value: string | string[] | null;
  onChange: (value: string | string[] | null) => void;
  choices: string[];
  allowOtherChoice?: boolean;
  checkAllThatApply?: boolean;
  disabled?: boolean;
}

const MultipleChoiceField: React.FC<MultipleChoiceFieldProps> = ({
  value,
  onChange,
  choices,
  allowOtherChoice = false,
  checkAllThatApply = false,
  disabled,
}) => {
  const theme = useTheme();

  if (!choices || choices.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
        No choices configured
      </Typography>
    );
  }

  const configuredChoices = choices.filter((choice) => choice.trim().length > 0);

  if (configuredChoices.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
        No choices configured
      </Typography>
    );
  }

  const selectedValues = Array.isArray(value) ? value : value != null ? [value] : [];
  const isConfiguredChoiceSelected = (choice: string) => selectedValues.includes(choice);
  const selectedOtherValue = allowOtherChoice
    ? selectedValues.find((selected) => !configuredChoices.includes(selected))
    : undefined;
  const isOtherSelected = allowOtherChoice && selectedOtherValue !== undefined;

  const updateSingleChoice = (choice: string) => {
    if (checkAllThatApply) {
      const nextValues = selectedValues.includes(choice)
        ? selectedValues.filter((selected) => selected !== choice)
        : [...selectedValues, choice];
      onChange(nextValues);
      return;
    }
    onChange(selectedValues[0] === choice ? null : choice);
  };

  const toggleOther = () => {
    if (!checkAllThatApply) {
      onChange(isOtherSelected ? null : '');
      return;
    }

    if (isOtherSelected) {
      onChange(selectedValues.filter((selected) => selected !== selectedOtherValue));
      return;
    }

    onChange([...selectedValues, '']);
  };

  const updateOtherText = (nextText: string) => {
    if (!checkAllThatApply) {
      onChange(nextText);
      return;
    }

    const nonOtherSelections = selectedValues.filter((selected) => configuredChoices.includes(selected));
    onChange([...nonOtherSelections, nextText]);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 1,
        mt: 2,
      }}
    >
      {configuredChoices.map((choice, index) => {
        const isSelected = isConfiguredChoiceSelected(choice);
        return (
          <Button
            key={`${choice}-${index}`}
            variant={isSelected ? 'contained' : 'outlined'}
            onClick={() => updateSingleChoice(choice)}
            disabled={disabled}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: isSelected ? 600 : 500,
              py: 1.25,
              borderColor: isSelected ? 'primary.main' : alpha(theme.palette.divider, 0.5),
              justifyContent: 'flex-start',
              '&:hover': {
                borderColor: 'primary.main',
              },
            }}
            fullWidth
          >
            {choice}
          </Button>
        );
      })}

      {allowOtherChoice && (
        <Button
          variant={isOtherSelected ? 'contained' : 'outlined'}
          onClick={toggleOther}
          disabled={disabled}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: isOtherSelected ? 600 : 500,
            py: 1.25,
            borderColor: isOtherSelected ? 'primary.main' : alpha(theme.palette.divider, 0.5),
            justifyContent: 'flex-start',
            '&:hover': {
              borderColor: 'primary.main',
            },
          }}
          fullWidth
        >
          Other
        </Button>
      )}

      {allowOtherChoice && isOtherSelected && (
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          value={selectedOtherValue || ''}
          onChange={(e) => updateOtherText(e.target.value)}
          disabled={disabled}
          autoFocus
        />
      )}
    </Box>
  );
};

export default MultipleChoiceField;
